import { useMemo, useState } from "react";
import { KeyboardArrowRight } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ENTRY_STATUS_LABELS, rowRole } from "../../../utils/commissionGrouping";
import { commissionStatusTone, formatDate, moneyAud } from "../../utils/portalHelpers";
import {
  PageHeader,
  StatCard,
  StatusChip,
  EmptyState,
  LoadingPanel,
  ErrorPanel,
} from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import {
  fetchPartnerCommissionEntries,
  fetchPartnerEarningsSummary,
  fetchPartnerPayouts,
} from "../../api/endpoints/commissions";

/** Entries per ledger page: every entry arrives whole, so every net shown is whole. */
const ENTRY_PAGE_SIZE = 10;

const FILTERS = [
  { value: "all", label: "All entries" },
  { value: "ACCRUED", label: "Awaiting approval" },
  { value: "APPROVED", label: "Approved" },
  { value: "PAID", label: "Paid" },
  { value: "CANCELLED", label: "Cancelled" },
];

const CHIP_TONES = { info: "sky", warning: "amber", success: "emerald", default: "neutral" };

const ENTRY_CHIP_TONES = {
  ACCRUED: "sky",
  APPROVED: "amber",
  PAID: "emerald",
  CANCELLED: "neutral",
  PART_APPROVED: "sky",
  PART_PAID: "amber",
};

/** Label | status | payout ref | amount — fixed, so opening an entry never reflows the columns. */
const LEDGER_GRID =
  "sm:grid sm:grid-cols-[minmax(0,1fr)_9.5rem_8rem_7.5rem] sm:items-center sm:gap-4";

/** A filter picks whole entries: the server folds the in-between states the same way. */
const countFor = (counts, value) => {
  const raw = counts || {};
  if (value === "all") return Number(raw.ALL ?? 0);
  if (value === "APPROVED") return Number(raw.APPROVED ?? 0) + Number(raw.PART_APPROVED ?? 0);
  if (value === "PAID") return Number(raw.PAID ?? 0) + Number(raw.PART_PAID ?? 0);
  return Number(raw[value] ?? 0);
};

const PAYOUTS_SIZE = 20;

const PaymentsPage = () => {
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState([]);
  const [page, setPage] = useState(0);
  const [payoutsPage, setPayoutsPage] = useState(0);

  const params = useMemo(
    () => ({
      page,
      size: ENTRY_PAGE_SIZE,
      ...(filter === "all" ? {} : { status: filter }),
    }),
    [page, filter]
  );

  const { data: summary } = useQuery(
    partnerQueryKeys.payouts({ view: "summary" }),
    fetchPartnerEarningsSummary,
    { refetchOnMount: true }
  );
  const {
    data: ledger,
    isLoading,
    isError,
  } = useQuery(
    partnerQueryKeys.commissions({ view: "entries", ...params }),
    () => fetchPartnerCommissionEntries(params),
    {
      keepPreviousData: true,
      refetchOnMount: true,
    }
  );

  const payoutsParams = useMemo(() => ({ page: payoutsPage, size: PAYOUTS_SIZE }), [payoutsPage]);
  const { data: payouts } = useQuery(
    partnerQueryKeys.payouts({ view: "list", ...payoutsParams }),
    () => fetchPartnerPayouts(payoutsParams),
    { keepPreviousData: true, refetchOnMount: true }
  );

  const totals = useMemo(() => {
    if (summary) {
      return {
        accrued: Number(summary.accrued) || 0,
        approved: Number(summary.approved) || 0,
        paid: Number(summary.paid) || 0,
        total:
          (Number(summary.accrued) || 0) +
          (Number(summary.approved) || 0) +
          (Number(summary.paid) || 0),
      };
    }
    const net = (ledger?.content || []).reduce((sum, entry) => sum + (Number(entry.net) || 0), 0);
    return { accrued: 0, approved: 0, paid: 0, total: net };
  }, [summary, ledger]);

  // The page arrives pre-grouped by the server; headers only re-collect each client's own
  // entries. Nets and counts ride on the entries from the server, so they stay true on any page.
  const clients = useMemo(() => {
    const blocks = new Map();
    for (const entry of ledger?.content || []) {
      const key = entry.clientId ?? entry.clientName ?? "unknown";
      if (!blocks.has(key)) {
        blocks.set(key, {
          clientId: entry.clientId ?? null,
          clientName: entry.clientName,
          entries: [],
          net: Number(entry.clientNet) || 0,
          count: entry.clientEntryCount ?? 0,
        });
      }
      blocks.get(key).entries.push(entry);
    }
    return [...blocks.values()];
  }, [ledger]);

  const totalPages = ledger?.totalPages ?? 0;

  const toggleExpanded = (entryId) =>
    setExpanded((prev) =>
      prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]
    );

  return (
    <div>
      <PageHeader title="Referral Payments" />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard
          label="Awaiting approval"
          value={moneyAud(totals.accrued)}
          hint={totals.accrued < 0 ? "A correction reduces an approved amount" : undefined}
          tone="info"
        />
        <StatCard label="Approved" value={moneyAud(totals.approved)} tone="warn" />
        <StatCard label="Paid" value={moneyAud(totals.paid)} tone="good" />
        <StatCard label="Total earned" value={moneyAud(totals.total)} tone="default" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Filter by status">
        {FILTERS.map((option) => {
          const active = filter === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setFilter(option.value);
                setPage(0);
              }}
              className={[
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold cursor-pointer transition-colors",
                active
                  ? "border-teal-700 bg-teal-700 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
              ].join(" ")}
            >
              {option.label}
              <span
                className={[
                  "rounded-full px-1.5 text-[10px] tabular-nums",
                  active ? "bg-white/20" : "bg-slate-100 text-slate-500",
                ].join(" ")}
              >
                {countFor(ledger?.statusCounts, option.value)}
              </span>
            </button>
          );
        })}
      </div>

      {isError ? (
        <ErrorPanel
          title="Couldn't load your ledger"
          description="Check your connection and try again."
        />
      ) : isLoading ? (
        <LoadingPanel label="Loading your payments…" />
      ) : clients.length === 0 ? (
        <EmptyState
          title={filter === "all" ? "No payments yet" : "Nothing under this filter"}
          description={
            filter === "all"
              ? "Commission appears here once the office records a referral amount against one of your clients."
              : "Try another status to see the rest of your entries."
          }
        />
      ) : (
        <div>
          <div
            className={`hidden ${LEDGER_GRID} px-4 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500`}
          >
            <span className="pl-7">Service</span>
            <span>Status</span>
            <span>Payout ref</span>
            <span className="text-right">Amount</span>
          </div>
          <ul className="list-none m-0 p-0 space-y-3">
            {clients.map((client) => (
              <ClientBlock
                key={client.clientId ?? client.clientName}
                client={client}
                expanded={expanded}
                onToggleExpanded={toggleExpanded}
              />
            ))}
          </ul>
          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 mt-4">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      )}

      <PayoutsPanel
        payouts={payouts?.content || []}
        page={payoutsPage}
        totalPages={payouts?.totalPages ?? 0}
        onPageChange={setPayoutsPage}
      />
    </div>
  );
};

/** What actually reached the partner, and the proof of each transfer. */
const PayoutsPanel = ({ payouts, page = 0, totalPages = 0, onPageChange }) => {
  if (payouts.length === 0) return null;
  return (
    <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <h2 className="px-4 py-3 m-0 text-sm font-semibold text-slate-900 border-b border-slate-100">
        Payouts sent
      </h2>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3 font-semibold">Reference</th>
            <th className="px-4 py-3 font-semibold">Paid on</th>
            <th className="px-4 py-3 font-semibold text-right">Amount</th>
            <th className="px-4 py-3 font-semibold">Method</th>
            <th className="px-4 py-3 font-semibold">Receipt</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map((payout) => (
            <tr key={payout.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-medium">{payout.reference || "—"}</td>
              <td className="px-4 py-3 text-slate-600">{formatDate(payout.paidDate)}</td>
              <td className="px-4 py-3 text-right tabular-nums font-semibold">
                {moneyAud(payout.amount)}
              </td>
              <td className="px-4 py-3 text-slate-600">
                {(payout.method || "").replace("_", " ").toLowerCase() || "—"}
              </td>
              <td className="px-4 py-3">
                {(payout.receiptDocuments || []).length === 0 ? (
                  <span className="text-xs text-slate-400">Not attached</span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {payout.receiptDocuments.map((url, index) => (
                      <a
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold text-teal-700"
                      >
                        {receiptLabel(url, index)}
                      </a>
                    ))}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-3">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => onPageChange(Math.max(page - 1, 0))}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-xs text-slate-500">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page + 1 >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
};

const receiptLabel = (url, index) => {
  const name = String(url || "")
    .split("/")
    .pop()
    ?.split("?")[0];
  return name || `Receipt ${index + 1}`;
};

/** A minus on a statement needs an account of itself: when, why, and who signed it off. */
const RowTrail = ({ row }) => {
  const parts = [];
  if (row.raisedByName) parts.push(`Raised by ${row.raisedByName}`);
  if (row.approvedByName) {
    parts.push(
      `Approved by ${row.approvedByName}${
        row.approvedAt ? ` on ${formatDate(row.approvedAt)}` : ""
      }`
    );
  }
  if (!row.note && parts.length === 0) return null;
  return (
    <div className="mt-0.5 text-[11px] text-slate-400">
      {row.note ? <div>{row.note}</div> : null}
      {parts.length > 0 ? <div>{parts.join(" · ")}</div> : null}
    </div>
  );
};

const ClientBlock = ({ client, expanded, onToggleExpanded }) => (
  <li className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50/70 border-b border-slate-100">
      <div className="min-w-0">
        {client.clientId ? (
          <Link
            to={`/partner/clients/${client.clientId}`}
            className="font-semibold text-teal-700 no-underline hover:underline"
          >
            {client.clientName}
          </Link>
        ) : (
          <span className="font-semibold text-slate-900">{client.clientName}</span>
        )}
        <span className="ml-2 text-xs text-slate-500">
          {client.count ?? client.entries.length} service
          {(client.count ?? client.entries.length) === 1 ? "" : "s"}
        </span>
      </div>
      <div className="text-right shrink-0">
        <p className="m-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Net</p>
        <p className="m-0 text-base font-bold tabular-nums text-slate-900">
          {moneyAud(client.net)}
        </p>
      </div>
    </div>

    <ul className="list-none m-0 p-0 divide-y divide-slate-100">
      {client.entries.map((entry) => {
        const payoutReference = entry.rows.map((row) => row.payoutReference).find(Boolean);
        return (
          <EntryBlock
            key={entry.entryId}
            entry={entry}
            isOpen={expanded.includes(entry.entryId)}
            payoutReference={payoutReference}
            onToggle={() => onToggleExpanded(entry.entryId)}
          />
        );
      })}
    </ul>
  </li>
);

const EntryBlock = ({ entry, isOpen, payoutReference, onToggle }) => (
  <li>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-label={`${isOpen ? "Hide" : "Show"} the ${entry.rows.length} row${
        entry.rows.length === 1 ? "" : "s"
      } behind ${entry.moduleLabel}`}
      className={`w-full ${LEDGER_GRID} flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 text-left text-sm bg-transparent border-0 cursor-pointer hover:bg-slate-50 transition-colors ${
        isOpen ? "bg-slate-50" : ""
      }`}
    >
      <span className="flex items-center gap-2 min-w-0 flex-1 sm:flex-none">
        <KeyboardArrowRight
          sx={{ fontSize: 20 }}
          className={`shrink-0 text-slate-400 transition-transform duration-200 ${
            isOpen ? "rotate-90" : ""
          }`}
        />
        <span className="min-w-0">
          <span className="block font-medium text-slate-800 truncate">{entry.moduleLabel}</span>
          {entry.correctionCount > 0 ? (
            <span className="block text-xs text-slate-500">
              {entry.correctionCount} correction{entry.correctionCount === 1 ? "" : "s"}
            </span>
          ) : null}
        </span>
      </span>
      <span className="order-last sm:order-none pl-7 sm:pl-0">
        <StatusChip tone={ENTRY_CHIP_TONES[entry.status] || "neutral"}>
          {ENTRY_STATUS_LABELS[entry.status] || entry.status}
        </StatusChip>
      </span>
      <span className="hidden sm:block text-xs text-slate-500 truncate">
        {payoutReference || "—"}
      </span>
      <span className="ml-auto sm:ml-0 text-right font-semibold tabular-nums text-slate-900">
        {moneyAud(entry.net)}
      </span>
    </button>

    {isOpen ? (
      <ol className="list-none m-0 px-4 pb-3 pt-1 bg-slate-50">
        {entry.rows.map((row, index) => (
          <li
            key={row.id}
            className={`${LEDGER_GRID} flex flex-wrap gap-x-3 gap-y-1 ml-7 border-l-2 border-slate-200 py-2 pl-4 text-xs`}
          >
            <div className="min-w-0 flex-1 sm:flex-none text-slate-600">
              <div className="font-medium text-slate-700">
                {rowRole(row, index)}
                {row.earnedAt ? (
                  <span className="font-normal text-slate-400"> · {formatDate(row.earnedAt)}</span>
                ) : null}
              </div>
              <RowTrail row={row} />
            </div>
            <span className="order-last sm:order-none">
              <StatusChip tone={CHIP_TONES[commissionStatusTone(row.status)] || "neutral"}>
                {row.status}
              </StatusChip>
            </span>
            <span className="hidden sm:block text-slate-500 truncate">
              {row.payoutReference || "—"}
            </span>
            <span
              className={`ml-auto sm:ml-0 text-right tabular-nums ${
                Number(row.amount) < 0 ? "text-rose-600" : "text-slate-700"
              }`}
            >
              {moneyAud(row.amount)}
            </span>
          </li>
        ))}
      </ol>
    ) : null}
  </li>
);

export default PaymentsPage;
